import React from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Check, MessageSquare, CheckCircle2, Clock, MapPin } from "lucide-react";
import { formatStringToDate } from "../../utils";

interface MessageCardProps {
  item: {
    id: number;
    jobTitle?: string;
    idealJobTitles?: string;
    lastCommentCreatedAt?: string;
    createdAt: string;
    isResolvedByOwner?: boolean;
    isResolvedByAuthor?: boolean;
    locationDisplayName?: string;
  };
  index: number;
  onView: () => void;
  onResolve: () => void;
  isRole?: boolean;
}

const MessageCard: React.FC<MessageCardProps> = ({
  item,
  index,
  onView,
  onResolve,
  isRole = true,
}) => {
  const isResolved = item.isResolvedByOwner || item.isResolvedByAuthor;
  const title = isRole ? item.jobTitle : item.idealJobTitles;
  const timestamp = item.lastCommentCreatedAt ?? item.createdAt;

  return (
    <div
      className={cn(
        // Base styling - matching Settings cards
        "relative",
        "bg-white dark:bg-slate-800/80",
        // Border
        "border border-slate-200 dark:border-slate-700",
        "rounded-xl",
        // Shadow
        "shadow-sm",
        // Spacing
        "mb-4 p-4",
        // Transitions
        "transition-all duration-200",
        // Active state for touch
        "active:scale-[0.99]",
        // Reduced motion
        "motion-reduce:transition-none"
      )}
    >
      {/* Header with status */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex-1 min-w-0">
          {/* Status Badge */}
          <div className="mb-2">
            {!isResolved ? (
              <span className={cn(
                "inline-flex items-center",
                "px-2.5 py-1 rounded-full",
                "text-xs font-semibold uppercase tracking-wide",
                "bg-teal-50 dark:bg-teal-950/50",
                "text-teal-700 dark:text-teal-300",
                "border border-teal-200 dark:border-teal-800"
              )}>
                <span className="w-1.5 h-1.5 rounded-full bg-teal-500 mr-1.5 animate-pulse" />
                Active Query
              </span>
            ) : (
              <span className={cn(
                "inline-flex items-center",
                "px-2.5 py-1 rounded-full",
                "text-xs font-semibold uppercase tracking-wide",
                "bg-slate-100 dark:bg-slate-700/50",
                "text-slate-500 dark:text-slate-400"
              )}>
                <CheckCircle2 className="w-3 h-3 mr-1.5" />
                Resolved
              </span>
            )}
          </div>

          {/* Title - serif font matching page headers */}
          <h3 className={cn(
            "font-serif text-lg font-medium leading-snug",
            "text-slate-800 dark:text-slate-100",
            "line-clamp-2"
          )}>
            {title}
          </h3>
        </div>

        {/* Visual indicator for active messages */}
        {!isResolved && (
          <div className={cn(
            "w-2.5 h-2.5 rounded-full shrink-0 mt-1",
            "bg-teal-500 dark:bg-teal-400",
            "shadow-[0_0_8px_rgba(20,184,166,0.5)]",
            "animate-pulse"
          )} />
        )}
      </div>

      {/* Metadata row */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mb-4">
        {/* Timestamp */}
        <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
          <Clock className="h-3.5 w-3.5" />
          <time className="text-sm">
            {formatStringToDate(timestamp)}
          </time>
        </div>

        {/* Location if available */}
        {item.locationDisplayName && (
          <div className="flex items-center gap-1.5 text-slate-500 dark:text-slate-400">
            <MapPin className="h-3.5 w-3.5" />
            <span className="text-sm truncate max-w-[180px]">
              {item.locationDisplayName}
            </span>
          </div>
        )}
      </div>

      {/* Action buttons - refined styling */}
      <div className="flex gap-3">
        {/* View Button */}
        <Button
          size="default"
          variant="outline"
          onClick={onView}
          className={cn(
            "flex-1 h-11",
            "border-slate-200 dark:border-slate-600",
            "text-slate-700 dark:text-slate-300",
            "hover:border-teal-600 hover:text-teal-600",
            "hover:bg-teal-50 dark:hover:bg-teal-950/30",
            "dark:hover:border-teal-500 dark:hover:text-teal-400",
            "transition-all duration-200",
            "font-medium"
          )}
        >
          <MessageSquare className="h-4 w-4 mr-2" />
          View Thread
        </Button>

        {/* Resolve Button */}
        {!isResolved ? (
          <Button
            size="default"
            onClick={onResolve}
            className={cn(
              "flex-1 h-11",
              "bg-teal-600 hover:bg-teal-700",
              "dark:bg-teal-600 dark:hover:bg-teal-500",
              "text-white",
              "shadow-sm hover:shadow-md",
              "transition-all duration-200",
              "font-medium"
            )}
          >
            <Check className="h-4 w-4 mr-2" />
            Resolve
          </Button>
        ) : (
          <Button
            size="default"
            variant="secondary"
            disabled
            className={cn(
              "flex-1 h-11",
              "bg-slate-100 dark:bg-slate-700/50",
              "text-slate-400 dark:text-slate-500",
              "cursor-not-allowed",
              "font-medium"
            )}
          >
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Resolved
          </Button>
        )}
      </div>
    </div>
  );
};

export default MessageCard;
